var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _MenuItem_instances, _MenuItem_attachListeners, _MenuItem_detachListeners;
import { attr, FASTElement, observable } from "@microsoft/fast-element";
import { keyArrowLeft, keyArrowRight, keyEnter, keySpace } from "@microsoft/fast-web-utilities";
import { toggleState } from "../../util/element-internals.js";
import { MenuItemRoles } from "./menu-item.options.js";
/**
 * MenuItem
 * @summary A MenuItem component that represents an item in a menu with various states and interactions.
 *
 * @example
 * ```html
 * <fabric-menu-item role="menuitem" disabled checked hidden>
 *   <span slot="start">Start content</span>
 *   Menu item content
 *   <span slot="end">End content</span>
 *   <span slot="submenu-glyph"></span>
 *   <div slot="submenu">Submenu content</div>
 * </fabric-menu-item>
 * ```
 *
 * @attr {boolean} disabled - The disabled state of the element.
 * @attr {MenuItemRole} role - The role of the element. Possible values: 'menuitem', 'menuitemcheckbox', 'menuitemradio'.
 * @attr {boolean} checked - The checked value of the element.
 * @attr {boolean} hidden - The hidden attribute.
 *
 * @prop {boolean} disabled - The disabled state of the element.
 * @prop {MenuItemRole} role - The role of the element.
 * @prop {boolean} checked - The checked value of the element.
 * @prop {boolean} hidden - The hidden attribute.
 * @prop {HTMLElement[]} slottedSubmenu - The submenu slotted content.
 * @prop {HTMLElement | undefined} submenu - The submenu element associated with this menu item.
 *
 * @slot - Default slot for menu item content.
 * @slot start - Slot for the start content.
 * @slot end - Slot for the end content.
 * @slot indicator - The checkbox or radio indicator.
 * @slot submenu-glyph - The submenu expand/collapse indicator.
 * @slot submenu - Slot for the submenu content.
 *
 * @csspart content - The main content of the menu item.
 *
 * @cssprop --indent - Custom property for indentation level (default: 0).
 * @cssprop --menu-max-height - Maximum height for submenu (default: auto).
 *
 * @internal connectedCallback - Called when the element is added to the document.
 * @internal disconnectedCallback - Called when the element is removed from the document.
 * @method disabledChanged - Handles changes to the disabled attribute.
 * @method roleChanged - Handles changes to the role attribute.
 * @method checkedChanged - Handles changes to the checked attribute.
 * @method slottedSubmenuChanged - Handles changes to the slotted submenu content.
 * @method handleMenuItemKeyDown - Handles keydown events for the menu item.
 * @method handleMenuItemClick - Handles click events for the menu item.
 * @method handleMouseOver - Handles mouse over events for the menu item.
 * @method handleMouseOut - Handles mouse out events for the menu item.
 * @method toggleHandler - Handles toggle events from submenu popover.
 *
 * @fires change - Fired when the checked state changes.
 *
 * @extends FASTElement
 * @tagname fabric-menu-item
 * @public
 */
export class MenuItem extends FASTElement {
    constructor() {
        super(...arguments);
        _MenuItem_instances.add(this);
        /**
         * The internal {@link https://developer.mozilla.org/docs/Web/API/ElementInternals | `ElementInternals`} instance for the component.
         *
         * @internal
         */
        this.elementInternals = this.attachInternals();
        /**
         * The role of the element.
         *
         * @public
         * @remarks
         * HTML Attribute: role
         */
        this.role = MenuItemRoles.menuitem;
        /**
         * The checked value of the element.
         *
         * @public
         * @remarks
         * HTML Attribute: checked
         */
        this.checked = false;
        /**
         * Handles keydown events for the menu item.
         *
         * @param e - The keyboard event
         * @returns `false` if the event was handled and should not propagate, `true` otherwise
         * @protected
         */
        this.handleMenuItemKeyDown = (e) => {
            var _a;
            if (e.defaultPrevented) {
                return false;
            }
            switch (e.key) {
                case keyEnter:
                case keySpace: {
                    this.invoke();
                    return false;
                }
                case keyArrowRight: {
                    if (!this.disabled && hasTogglePopover(this.submenu)) {
                        this.submenu.togglePopover(true);
                        this.submenu.focus();
                    }
                    return false;
                }
                case keyArrowLeft: {
                    const parent = this.parentElement;
                    if (hasTogglePopover(parent)) {
                        parent.togglePopover(false);
                        (_a = parent.parentElement) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                    return false;
                }
            }
            return true;
        };
        /**
         * Handles click events for the menu item.
         *
         * @param e - The mouse event
         * @returns `false` if the event was handled and should not propagate
         * @protected
         */
        this.handleMenuItemClick = (e) => {
            if (e.defaultPrevented || this.disabled) {
                return false;
            }
            this.invoke();
            return false;
        };
        /**
         * Handles mouse over events for the menu item.
         * Opens any associated submenu when hovering over the item.
         *
         * @param e - The mouse event (unused but required for event handler signature)
         * @returns `false` if the event was handled and should not propagate
         * @protected
         */
        this.handleMouseOver = () => {
            var _a;
            if (this.disabled) {
                return false;
            }
            (_a = this.submenu) === null || _a === void 0 ? void 0 : _a.togglePopover(true);
            return false;
        };
        /**
         * Handles mouse out events for the menu item.
         * Closes any associated submenu when the mouse leaves the item,
         * unless the focus remains within the menu item.
         *
         * @param e - The mouse event (unused but required for event handler signature)
         * @returns `false` if the event was handled and should not propagate
         * @protected
         */
        this.handleMouseOut = () => {
            var _a;
            if (this.contains(document.activeElement)) {
                return false;
            }
            (_a = this.submenu) === null || _a === void 0 ? void 0 : _a.togglePopover(false);
            return false;
        };
        /**
         * Handles toggle events from associated submenu popover.
         * Sets up required ARIA attributes and tab navigation when submenu opens/closes.
         *
         * @param e - The toggle event from the submenu popover
         * @protected
         */
        this.toggleHandler = (e) => {
            if ("newState" in e && e.newState === "open") {
                this.setAttribute("tabindex", "-1");
                this.elementInternals.ariaExpanded = "true";
                this.setSubmenuPosition();
            }
            if ("newState" in e && e.newState === "closed") {
                this.elementInternals.ariaExpanded = "false";
                this.setAttribute("tabindex", "0");
            }
        };
        /**
         * Invokes the appropriate action based on the menu item's role.
         * For checkbox items, toggles the checked state.
         * For radio items, sets the checked state to true if not already checked.
         * For regular menu items, opens submenus or emits a change event.
         *
         * @internal
         */
        this.invoke = () => {
            if (this.disabled) {
                return;
            }
            switch (this.role) {
                case MenuItemRoles.menuitemcheckbox:
                    this.checked = !this.checked;
                    break;
                case MenuItemRoles.menuitem:
                    if (this.submenu) {
                        this.submenu.togglePopover(true);
                        this.submenu.focus();
                        break;
                    }
                    this.$emit("change");
                    break;
                case MenuItemRoles.menuitemradio:
                    if (!this.checked) {
                        this.checked = true;
                    }
                    break;
            }
        };
        /**
         * Sets the fallback position of the submenu when CSS anchor positioning is not supported.
         * Calculates optimal positioning to keep the submenu within the viewport bounds.
         * Positions the submenu to the inline-end by default, but adjusts based on available space.
         *
         * @internal
         */
        this.setSubmenuPosition = () => {
            if (!CSS.supports("anchor-name", "--anchor") && !!this.submenu) {
                const thisRect = this.getBoundingClientRect();
                const thisSubmenuRect = this.submenu.getBoundingClientRect();
                const inlineEnd = getComputedStyle(this).direction === "ltr" ? "right" : "left";
                // If an open submenu is too wide for the viewport, move it above.
                if (thisRect.width + thisSubmenuRect.width > window.innerWidth * 0.75) {
                    this.submenu.style.translate = "0 -100%";
                    return;
                }
                // If the open submenu is overflows the inline-end of the window (e.g. justify-content: end),
                // move to inline-start of menu item
                if (thisRect[inlineEnd] + thisSubmenuRect.width > window.innerWidth) {
                    this.submenu.style.translate = "-100% 0";
                    return;
                }
                // Default to inline-end of menu item
                this.submenu.style.translate = `${thisRect.width - 8}px 0`;
            }
        };
    }
    /**
     * Handles changes to disabled attribute custom states and element internals
     * @param prev - the previous state
     * @param next - the next state
     */
    disabledChanged(prev, next) {
        this.elementInternals.ariaDisabled = next ? `${next}` : null;
        toggleState(this.elementInternals, "disabled", next);
    }
    /**
     * Handles changes to role attribute element internals properties
     * @param prev - the previous state
     * @param next - the next state
     */
    roleChanged(prev, next) {
        this.elementInternals.role = next !== null && next !== void 0 ? next : MenuItemRoles.menuitem;
        const checkableMenuItem = (next !== null && next !== void 0 ? next : MenuItemRoles.menuitem) !== MenuItemRoles.menuitem;
        // Initialize ariaChecked for checkable menu items
        this.elementInternals.ariaChecked = checkableMenuItem
            ? `${!!this.checked}`
            : null;
        toggleState(this.elementInternals, "checked", checkableMenuItem ? this.checked : false);
    }
    /**
     * Handles changes to checked attribute custom states and element internals.
     * @param prev - the previous state
     * @param next - the next state
     */
    checkedChanged(prev, next) {
        const checkableMenuItem = this.role !== MenuItemRoles.menuitem;
        this.elementInternals.ariaChecked = checkableMenuItem ? `${!!next}` : null;
        toggleState(this.elementInternals, "checked", checkableMenuItem ? next : false);
        if (this.$fastController.isConnected) {
            this.$emit("change", next, { bubbles: true });
        }
    }
    /**
     * Sets the submenu and updates its position.
     *
     * @internal
     */
    slottedSubmenuChanged(prev, next) {
        var _a;
        (_a = this.submenu) === null || _a === void 0 ? void 0 : _a.removeEventListener("toggle", this.toggleHandler);
        if (next.length) {
            this.submenu = next[0];
            this.submenu.toggleAttribute("popover", true);
            this.submenu.addEventListener("toggle", this.toggleHandler);
            this.elementInternals.ariaHasPopup = "menu";
            toggleState(this.elementInternals, "submenu", true);
        }
        else {
            this.elementInternals.ariaHasPopup = null;
            toggleState(this.elementInternals, "submenu", false);
        }
    }
    /**
     * @public
     * Sets the role and registers event listeners.
     */
    connectedCallback() {
        var _a;
        super.connectedCallback();
        this.elementInternals.role = (_a = this.role) !== null && _a !== void 0 ? _a : MenuItemRoles.menuitem;
        __classPrivateFieldGet(this, _MenuItem_instances, "m", _MenuItem_attachListeners).call(this);
    }
    /**
     * Removes event listeners when component is disconnected.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        __classPrivateFieldGet(this, _MenuItem_instances, "m", _MenuItem_detachListeners).call(this);
    }
}
_MenuItem_instances = new WeakSet(), _MenuItem_attachListeners = function _MenuItem_attachListeners() {
    this.addEventListener("keydown", this.handleMenuItemKeyDown);
    this.addEventListener("click", this.handleMenuItemClick);
    this.addEventListener("mouseover", this.handleMouseOver);
    this.addEventListener("mouseout", this.handleMouseOut);
    this.addEventListener("toggle", this.toggleHandler);
}, _MenuItem_detachListeners = function _MenuItem_detachListeners() {
    this.removeEventListener("keydown", this.handleMenuItemKeyDown);
    this.removeEventListener("click", this.handleMenuItemClick);
    this.removeEventListener("mouseover", this.handleMouseOver);
    this.removeEventListener("mouseout", this.handleMouseOut);
    this.removeEventListener("toggle", this.toggleHandler);
};
__decorate([
    attr({ mode: "boolean" })
], MenuItem.prototype, "disabled", void 0);
__decorate([
    attr
], MenuItem.prototype, "role", void 0);
__decorate([
    attr({ mode: "boolean" })
], MenuItem.prototype, "checked", void 0);
__decorate([
    attr({ mode: "boolean" })
], MenuItem.prototype, "hidden", void 0);
__decorate([
    observable
], MenuItem.prototype, "slottedSubmenu", void 0);
__decorate([
    observable
], MenuItem.prototype, "submenu", void 0);
/*
 * Runtime type-guard that returns true only if the given value is an HTMLElement
 * with a callable `togglePopover` method—preventing crashes in browsers that lack the Popover API.
 */
function hasTogglePopover(el) {
    return (typeof el === "object" &&
        el !== null &&
        "togglePopover" in el &&
        typeof el.togglePopover === "function");
}
//# sourceMappingURL=menu-item.js.map